import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FlagIcon from '@mui/icons-material/Flag';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const statCards = [
    { label: 'Total Exceptions', value: '1,245', sub: '▲ 12.4% vs Previous Period', up: true, color: '#d32f2f', bg: '#ffebee', icon: <ErrorOutlineIcon sx={{ fontSize: 22, color: '#d32f2f' }} /> },
    { label: 'High Risk Alerts', value: '156', sub: '▲ 18.7% vs Previous Period', up: true, color: '#d32f2f', bg: '#ffebee', icon: <NotificationsActiveIcon sx={{ fontSize: 22, color: '#d32f2f' }} /> },
    { label: 'Medium Risk Alerts', value: '342', sub: '▼ 5.3% vs Previous Period', up: false, color: '#ed6c02', bg: '#fff3e0', icon: <FlagIcon sx={{ fontSize: 22, color: '#ed6c02' }} /> },
    { label: 'Low Risk Alerts', value: '747', sub: '▲ 6.2% vs Previous Period', up: true, color: '#2e7d32', bg: '#e8f5e9', icon: <VerifiedIcon sx={{ fontSize: 22, color: '#2e7d32' }} /> },
    { label: 'Resolved Exceptions', value: '892', sub: '▲ 9.8% vs Previous Period', up: true, color: '#5c35a0', bg: '#ede7f6', icon: <SecurityIcon sx={{ fontSize: 22, color: '#5c35a0' }} /> },
];

const leakageIndicators = [
    { label: 'No Ticket Cases', value: '1,285', sub: '▲ 15.6% vs Last Month', up: true, color: '#d32f2f', barColor: '#d32f2f', pct: 65 },
    { label: 'Underpayment Cases', value: '870', sub: '▲ 8.3% vs Last Month', up: true, color: '#ed6c02', barColor: '#ed6c02', pct: 45 },
    { label: 'Failed Taps', value: '2,345', sub: '▲ 21.7% vs Last Month', up: true, color: '#ed6c02', barColor: '#f9a825', pct: 80 },
    { label: 'Conductor Variance', value: 'Rs. 3,245,600', sub: '▲ 12.2% vs Last Month', up: true, color: '#2e7d32', barColor: '#2e7d32', pct: 55 },
];

const riskSummaryRows = [
    { type: 'Failed Taps', count: 2345, impact: 'Rs. 1,245,600' },
    { type: 'No Ticket Cases', count: 1285, impact: 'Rs. 2,156,780' },
    { type: 'Underpayments', count: 870, impact: 'Rs. 1,025,450' },
    { type: 'Breakdown Loss', count: 186, impact: 'Rs. 3,650,320' },
];

const severityColor = (s) => {
    if (s === 'High') return { backgroundColor: '#ffebee', color: '#d32f2f' };
    if (s === 'Medium') return { backgroundColor: '#fff3e0', color: '#ed6c02' };
    return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
};

const statusColor = (s) => {
    if (s === 'Open') return { backgroundColor: '#ffebee', color: '#d32f2f' };
    if (s === 'In Progress') return { backgroundColor: '#fff3e0', color: '#ed6c02' };
    return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
};

const detailRows = [
    { datetime: '30/05/2026 10:15', type: 'Failed Tap', severity: 'High', depot: 'Colombo Depot', route: 'R-001', bus: 'NB-2567', conductor: 'EMP1025 / POS-01', desc: 'High failure rate detected (8.7%)', impact: '125,600.00', status: 'Open', detectedBy: 'System' },
    { datetime: '30/05/2026 09:40', type: 'No Ticket Case', severity: 'High', depot: 'Kandy Depot', route: 'R-017', bus: 'NB-1123', conductor: 'EMP1044 / POS-03', desc: 'Multiple no ticket cases detected', impact: '256,780.00', status: 'Open', detectedBy: 'Inspector App' },
    { datetime: '30/05/2026 09:20', type: 'Underpayment', severity: 'Medium', depot: 'Kurunegala Depot', route: 'R-025', bus: 'NB-3344', conductor: 'EMP1077 / POS-02', desc: 'Fare underpayment detected', impact: '135,450.00', status: 'In Progress', detectedBy: 'System' },
    { datetime: '30/05/2026 08:55', type: 'Breakdown Impact', severity: 'High', depot: 'Matara Depot', route: 'R-085', bus: 'NB-8891', conductor: '—', desc: 'Breakdown caused trip cancellation', impact: '325,600.00', status: 'Open', detectedBy: 'Depot' },
    { datetime: '30/05/2026 08:30', type: 'Conductor Variance', severity: 'Medium', depot: 'Galle Depot', route: 'R-003', bus: 'NB-9901', conductor: 'EMP1090 / POS-04', desc: 'Revenue variance above threshold', impact: '98,750.00', status: 'In Progress', detectedBy: 'System' },
    { datetime: '29/05/2026 21:10', type: 'Failed Tap', severity: 'Medium', depot: 'Jaffna Depot', route: 'R-110', bus: 'NB-6673', conductor: 'EMP1033 / POS-01', desc: 'Failure rate spike (5.6%)', impact: '85,230.00', status: 'Open', detectedBy: 'System' },
    { datetime: '29/05/2026 20:45', type: 'No Ticket Case', severity: 'High', depot: 'Batticaloa Depot', route: 'R-118', bus: 'NB-7782', conductor: 'EMP1012 / POS-02', desc: 'High number of no ticket cases', impact: '210,900.00', status: 'Resolved', detectedBy: 'Inspector App' },
    { datetime: '29/05/2026 20:20', type: 'Underpayment', severity: 'Low', depot: 'Anuradhapura Depot', route: 'R-125', bus: 'NB-4455', conductor: 'EMP1044 / POS-03', desc: 'Minor underpayment variance', impact: '22,450.00', status: 'Resolved', detectedBy: 'System' },
    { datetime: '29/05/2026 18:45', type: 'Failed Tap', severity: 'High', depot: 'Colombo Depot', route: 'R-001', bus: 'NB-2210', conductor: 'EMP1025 / POS-01', desc: 'Repeated tap failures on route', impact: '145,300.00', status: 'Open', detectedBy: 'System' },
    { datetime: '29/05/2026 17:30', type: 'Conductor Variance', severity: 'Medium', depot: 'Kandy Depot', route: 'R-017', bus: 'NB-1456', conductor: 'EMP1055 / POS-02', desc: 'Cash variance exceeds limit', impact: '67,890.00', status: 'In Progress', detectedBy: 'System' },
    { datetime: '29/05/2026 16:15', type: 'No Ticket Case', severity: 'Medium', depot: 'Negombo Depot', route: 'R-042', bus: 'NB-3312', conductor: 'EMP1078 / POS-03', desc: 'Suspected fare evasion cluster', impact: '112,400.00', status: 'Open', detectedBy: 'Inspector App' },
    { datetime: '29/05/2026 14:50', type: 'Breakdown Impact', severity: 'Low', depot: 'Galle Depot', route: 'R-055', bus: 'NB-6678', conductor: '—', desc: 'Minor breakdown, partial trip loss', impact: '34,200.00', status: 'Resolved', detectedBy: 'Depot' },
];

const usePagination = (defaultRows = 10) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRows);
    return {
        page, rowsPerPage,
        handleChangePage: (_, p) => setPage(p),
        handleChangeRowsPerPage: (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); },
        startIndex: page * rowsPerPage,
    };
};

const RiskExceptionsPage = () => {
    const [depot, setDepot] = useState('');
    const [route, setRoute] = useState('');
    const [bus, setBus] = useState('');
    const [conductor, setConductor] = useState('');
    const [riskType, setRiskType] = useState('');

    const detailPag = usePagination(10);

    const thSx = { py: 1 };
    const tdSx = { py: 0 };

    return (
        <Container component="main" maxWidth={false} sx={{ py: 0, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page Header ── */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Risk &amp; Exceptions</Typography>
                </Box>

                {/* ── Filter Bar ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)', md: 'repeat(5,1fr) auto auto' }, gap: 2, width: '100%', mb: 3, alignItems: 'center' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Depot</InputLabel>
                        <Select value={depot} onChange={e => setDepot(e.target.value)} label="Depot">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="colombo">Colombo Depot</MenuItem>
                            <MenuItem value="kandy">Kandy Depot</MenuItem>
                            <MenuItem value="kurunegala">Kurunegala Depot</MenuItem>
                            <MenuItem value="matara">Matara Depot</MenuItem>
                            <MenuItem value="galle">Galle Depot</MenuItem>
                            <MenuItem value="negombo">Negombo Depot</MenuItem>
                            <MenuItem value="jaffna">Jaffna Depot</MenuItem>
                            <MenuItem value="batticaloa">Batticaloa Depot</MenuItem>
                            <MenuItem value="anuradhapura">Anuradhapura Depot</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Route</InputLabel>
                        <Select value={route} onChange={e => setRoute(e.target.value)} label="Route">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="R-001">R-001 – Colombo – Kandy</MenuItem>
                            <MenuItem value="R-017">R-017 – Kandy – Kurunegala</MenuItem>
                            <MenuItem value="R-025">R-025 – Kurunegala – Puttalam</MenuItem>
                            <MenuItem value="R-085">R-085 – Matara – Akuressa</MenuItem>
                            <MenuItem value="R-110">R-110 – Monaragala – Badulla</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Bus</InputLabel>
                        <Select value={bus} onChange={e => setBus(e.target.value)} label="Bus">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="NB-2567">NB-2567</MenuItem>
                            <MenuItem value="NB-1123">NB-1123</MenuItem>
                            <MenuItem value="NB-3344">NB-3344</MenuItem>
                            <MenuItem value="NB-8891">NB-8891</MenuItem>
                            <MenuItem value="NB-9901">NB-9901</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Conductor</InputLabel>
                        <Select value={conductor} onChange={e => setConductor(e.target.value)} label="Conductor">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="EMP1025">EMP1025</MenuItem>
                            <MenuItem value="EMP1044">EMP1044</MenuItem>
                            <MenuItem value="EMP1077">EMP1077</MenuItem>
                            <MenuItem value="EMP1090">EMP1090</MenuItem>
                            <MenuItem value="EMP1033">EMP1033</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Risk Type</InputLabel>
                        <Select value={riskType} onChange={e => setRiskType(e.target.value)} label="Risk Type">
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="failed_tap">Failed Tap</MenuItem>
                            <MenuItem value="no_ticket">No Ticket Case</MenuItem>
                            <MenuItem value="underpayment">Underpayment</MenuItem>
                            <MenuItem value="breakdown">Breakdown Impact</MenuItem>
                            <MenuItem value="conductor_variance">Conductor Variance</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained"
                        sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 'bold', height: 40, backgroundColor: '#3f51b5', '&:hover': { backgroundColor: '#303f9f' }, whiteSpace: 'nowrap', px: 3 }}>
                        Apply
                    </Button>
                    <Button variant="outlined"
                        sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 40, whiteSpace: 'nowrap', px: 3 }}>
                        Reset
                    </Button>
                </Box>

                {/* ── Stat Cards ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(5,1fr)' }, gap: 2, width: '100%', mb: 3 }}>
                    {statCards.map((card) => (
                        <Paper key={card.label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{ backgroundColor: card.bg, borderRadius: 1.5, p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    {card.icon}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>{card.label}</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2, mb: 0.5 }}>
                                {card.value}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {card.up ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} /> : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                <Typography variant="caption" sx={{ color: card.up ? '#2e7d32' : '#d32f2f', fontWeight: 600, fontSize: 11 }}>
                                    {card.sub}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* ── Revenue Leakage Indicators + Risk Summary ── */}
                <Grid container sx={{ width: '100%', mb: 3 }}>

                    {/* Revenue Leakage Indicators */}
                    <Grid item xs={12} md={12} sx={{mb:3}}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                Revenue Leakage Indicators (MTD) ℹ
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                                {leakageIndicators.map((ind) => (
                                    <Box key={ind.label}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, mb: 0.5 }}>{ind.label}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: ind.color, lineHeight: 1.2, mb: 0.3 }}>{ind.value}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1 }}>
                                            {ind.up ? <TrendingUpIcon sx={{ fontSize: 12, color: '#2e7d32' }} /> : <TrendingDownIcon sx={{ fontSize: 12, color: '#d32f2f' }} />}
                                            <Typography variant="caption" sx={{ color: ind.up ? '#2e7d32' : '#d32f2f', fontWeight: 600, fontSize: 11 }}>{ind.sub}</Typography>
                                        </Box>
                                        <Box sx={{ height: 4, borderRadius: 2, backgroundColor: '#f0f0f0' }}>
                                            <Box sx={{ height: '100%', borderRadius: 2, backgroundColor: ind.barColor, width: `${ind.pct}%` }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Risk Summary Table */}
                    <Grid item xs={12} md={12} sx={{mb:3}}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Risk Summary (MTD)</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                        <TableCell sx={{ py: 1, fontWeight: 600 }}>Risk Type</TableCell>
                                        <TableCell sx={{ py: 1, fontWeight: 600 }} align="right">Count</TableCell>
                                        <TableCell sx={{ py: 1, fontWeight: 600 }} align="right">Estimated Impact (Rs.)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {riskSummaryRows.map((row) => (
                                        <TableRow key={row.type} hover>
                                            <TableCell sx={{ py: 0.5, fontSize: 13 }}>{row.type}</TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: 13 }} align="right">{row.count.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: 13 }} align="right">{row.impact}</TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Total row */}
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ py: 1, fontWeight: 700, color: '#1976d2' }}>Total Estimated Impact</TableCell>
                                        <TableCell sx={{ py: 1 }} />
                                        <TableCell sx={{ py: 1, fontWeight: 700, color: '#1976d2' }} align="right">Rs. 8,078,150</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Risk & Exception Details Table ── */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Risk &amp; Exception Details</Typography>
                </Box>

                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                <TableCell sx={thSx}>Date / Time</TableCell>
                                <TableCell sx={thSx}>Risk Type</TableCell>
                                <TableCell sx={thSx}>Severity</TableCell>
                                <TableCell sx={thSx}>Depot</TableCell>
                                <TableCell sx={thSx}>Route Code</TableCell>
                                <TableCell sx={thSx}>Bus No.</TableCell>
                                <TableCell sx={thSx}>Conductor / POS ID</TableCell>
                                <TableCell sx={thSx}>Description</TableCell>
                                <TableCell sx={thSx}>Impact (Rs.)</TableCell>
                                <TableCell sx={thSx}>Status</TableCell>
                                <TableCell sx={thSx}>Detected By</TableCell>
                                <TableCell sx={thSx} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detailRows
                                .slice(detailPag.startIndex, detailPag.startIndex + detailPag.rowsPerPage)
                                .map((row, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.datetime}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.type}</TableCell>
                                        <TableCell sx={tdSx}>
                                            <Chip label={row.severity} size="small" sx={{ ...severityColor(row.severity), fontWeight: 600, fontSize: 11 }} />
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.depot}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.route}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.bus}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.conductor}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.desc}</TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.impact}</TableCell>
                                        <TableCell sx={tdSx}>
                                            <Chip label={row.status} size="small" sx={{ ...statusColor(row.status), fontWeight: 600, fontSize: 11 }} />
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, fontSize: 12 }}>{row.detectedBy}</TableCell>
                                        <TableCell sx={tdSx} align="right">
                                            <IconButton size="small" color="primary"><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        showFirstButton showLastButton
                        component="div"
                        count={245}
                        page={detailPag.page}
                        onPageChange={detailPag.handleChangePage}
                        rowsPerPage={detailPag.rowsPerPage}
                        onRowsPerPageChange={detailPag.handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>

            </Box>
        </Container>
    );
};

export default RiskExceptionsPage;